import {
  ProcessCellForExportParams,
  GridApi,
  PostSortRowsParams,
} from 'ag-grid-community'
import { useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { useIntl } from 'react-intl'
import _ from 'lodash'
import { TAG_DELIMITER } from '../../../../lib/functions/tag'
import { format as commentFormat } from '../../../../utils/comment'
import { exportExcel } from '../../../containers/BulkSheet/excel'
import { LEVEL_SEPARATOR } from '../../../containers/BulkSheet/const'
import { ColumnType } from '../../../containers/commons/AgGrid'
import { getLabel } from '../../../../lib/commons/i18nLabel'
import { CustomEnumValue } from '../../../../domain/value-object/CustomEnumValue'
import { useProductBacklogRepository } from '../../../../services/adaptors/productBacklogRepositoryAdaptor'
import { ProductBacklogEntity } from '../../../../domain/entity/ProductBacklogEntity'
import { SprintDetail } from '../../../../lib/functions/sprint'
import { addGlobalMessage, MessageLevel } from '../../../../store/messages'
import { TeamProps } from '../../../../lib/functions/team'
import {
  ProductBacklogItemRow,
  ProductBacklogRow,
  UNSET_SPRINT_ROW,
  groupPbisBySprintUuid,
  buildSprintRows,
} from '../productBacklog'
import { FIELD_WBSITEM_SPRINTS } from '../gridOptions/columnDefs'

export const MULTI_PROPERTY_DELIMITER: string = ','

/**
 * customExportValues をラップし、rowNumber 列に対して1始まりの連番を返す。
 * processCellCallback は行×列の順で呼ばれるため、
 * 前回と異なる行に移ったタイミングでカウンターをインクリメントする。
 *
 * エクスポート1回につき1つのクロージャーインスタンスが生成され、
 * 全セルの処理で共有される。
 */
const wrapWithRowNumber = (
  base: (
    params: ProcessCellForExportParams<ProductBacklogItemRow>
  ) => string | number | undefined
): ((
  params: ProcessCellForExportParams<ProductBacklogItemRow>
) => string | number | undefined) => {
  let rowCounter = 0
  let lastNodeId: string | undefined
  return (params: ProcessCellForExportParams<ProductBacklogItemRow>) => {
    const nodeId = params.node?.id ?? undefined
    if (nodeId !== lastNodeId) {
      rowCounter++
      lastNodeId = nodeId
    }
    if (params.column.getColId() === 'rowNumber') {
      return rowCounter
    }
    return base(params)
  }
}

export const useExcel = (gridApi: GridApi | undefined) => {
  const customExportValues = useCallback(
    (params: ProcessCellForExportParams<ProductBacklogItemRow>) => {
      if (!params.node) return undefined
      const columnId: string = params.column.getColId()
      const colDef = params.column.getColDef()
      const { cellEditorParams, type } = colDef
      const row: ProductBacklogItemRow | undefined = params.node.data

      if (columnId === 'ag-Grid-AutoColumn') {
        const rowData = params.node.data as ProductBacklogRow | undefined
        if (!rowData) return ''
        if (rowData.rowType === 'sprint') {
          return rowData.name
        }
        return `${LEVEL_SEPARATOR.repeat(
          params.node?.uiLevel || params.node?.level || 0
        )}${rowData.wbsItem?.displayName ?? ''}`
      }
      // rowNumber は wrapWithRowNumber で連番を振るため、ここでは処理しない
      if (columnId === 'wbsItem.type') {
        return row?.wbsItem?.baseWbsItemType?.getNameWithSuffix() ?? ' '
      }
      if (columnId === 'wbsItem.ticketType') {
        const wbsItemType = row?.wbsItem?.wbsItemType
        return wbsItemType?.isTicket() ? wbsItemType?.name : ' '
      }
      if (columnId === 'wbsItem.tags') {
        return params.value?.map(v => v.name)?.join(TAG_DELIMITER)
      }
      if (columnId === 'commentSummary.latestComment') {
        return params.value ? commentFormat(params.value) : ''
      }
      if (columnId === 'wbsItem.watchers') {
        return (
          params.value?.map(v => v.name).join(MULTI_PROPERTY_DELIMITER) ?? ''
        )
      }
      if (columnId === FIELD_WBSITEM_SPRINTS) {
        if (!params.value) return ''
        if (Array.isArray(params.value)) {
          return params.value
            .map((sprint: SprintDetail) => sprint.name)
            .join(',')
        }
        return String(params.value)
      }
      if (['createdBy', 'updatedBy'].includes(columnId)) {
        return params.value?.name ?? undefined
      }
      if (!type) {
        if (cellEditorParams) {
          const { valuesAllowed, entity } = cellEditorParams
          if (valuesAllowed) {
            // For extension select column
            const options: CustomEnumValue[] = valuesAllowed ?? []
            const option = options.find(v => v.value === params.value)
            return option?.name || ''
          }
          if (entity) {
            // For extension EntitySearch column
            const options = params.context[entity] ?? []
            const option = options.find(v =>
              [params.value, params.value?.uuid].includes(v.uuid)
            )
            return (
              getLabel(option?.nameI18n) ||
              option?.displayName ||
              option?.name ||
              ''
            )
          }
        }
        if (typeof params.value === 'object') {
          return params.value.name ?? params.value.displayName ?? undefined
        }
      } else {
        if (type.includes(ColumnType.multiSelect)) {
          return (
            params?.value?.map(v => v.name).join(MULTI_PROPERTY_DELIMITER) ?? ''
          )
        }
      }
      return undefined
    },
    []
  )

  const onExcelExport = useCallback(
    (colIds: string[]) => {
      if (!gridApi) return

      exportExcel({
        fileNamePrefix: 'productBacklog_edit',
        gridApi,
        exportColIds: colIds,
        getCustomExportValue: wrapWithRowNumber(customExportValues),
      })
    },
    [gridApi, customExportValues]
  )

  return { onExcelExport, customExportValues }
}

// --- 一括データ出力 ---

/**
 * ProductBacklogEntity → ProductBacklogRow[] に変換する
 */
const buildProductBacklogRows = (
  productBacklog: ProductBacklogEntity,
  teamSprints: SprintDetail[],
  teamUuid: string,
  teamName: string
): ProductBacklogRow[] => {
  const productBacklogItems = productBacklog.productBacklogItems || []
  const sortedSprints = [...teamSprints].sort((a, b) => a.num - b.num)
  const unsetSprintRow = _.cloneDeep(UNSET_SPRINT_ROW)
  // チームごとにユニークなUUIDにする（複数チームのunset行が衝突しないように）
  unsetSprintRow.uuid = `${UNSET_SPRINT_ROW.uuid}_${teamUuid}`
  unsetSprintRow.treeValue = [unsetSprintRow.uuid]

  const { spiMapBySprintUuid } = groupPbisBySprintUuid(
    productBacklogItems,
    unsetSprintRow.uuid
  )

  return buildSprintRows(
    sortedSprints,
    unsetSprintRow,
    spiMapBySprintUuid,
    teamName
  )
}

type BulkExcelExportParams = {
  teams: TeamProps[]
  allSprints: SprintDetail[]
  gridApi: GridApi | undefined
  customExportValues: (
    params: ProcessCellForExportParams<ProductBacklogItemRow>
  ) => string | number | undefined
  setLoading: (loading: boolean) => void
}

/** 全チームのプロダクトバックログを取得し、1つの行配列に合体する */
const fetchAllTeamRows = async (
  teams: TeamProps[],
  allSprints: SprintDetail[],
  getByTeamUuid: (teamUuid: string) => Promise<ProductBacklogEntity | undefined>
): Promise<ProductBacklogRow[]> => {
  const allRows: ProductBacklogRow[] = []
  const sortedTeams = [...teams].sort((a, b) =>
    a.displayName.localeCompare(b.displayName)
  )
  for (const team of sortedTeams) {
    const teamSprints = allSprints.filter(s => s.teamUuid === team.uuid)
    const productBacklog = await getByTeamUuid(team.uuid)
    if (!productBacklog) continue

    const rows = buildProductBacklogRows(
      productBacklog,
      teamSprints,
      team.uuid,
      team.displayName
    )
    allRows.push(...rows)
  }
  return allRows
}

/** グリッドの現在状態（rowData + 展開状態）をスナップショットとして取得する */
const captureGridState = (gridApi: GridApi) => {
  const rowData: ProductBacklogRow[] = []
  const expandedNodeIds = new Set<string>()
  gridApi.forEachNode(node => {
    rowData.push(node.data)
    if (node.expanded && node.id) {
      expandedNodeIds.add(node.id)
    }
  })
  return { rowData, expandedNodeIds }
}

/** グリッドを元の状態に復元する */
const restoreGridState = async (
  gridApi: GridApi,
  rowData: ProductBacklogRow[],
  expandedNodeIds: Set<string>,
  originalPostSortRows: ((params: PostSortRowsParams) => void) | undefined
) => {
  gridApi.setGridOption('postSortRows', originalPostSortRows)
  gridApi.setGridOption('rowData', rowData)
  // AG Grid の setGridOption('rowData') は非同期でDOMを更新するため、
  // マイクロタスクを挟んでグリッドの描画完了を待つ
  await new Promise<void>(resolve => setTimeout(resolve, 0))

  gridApi.forEachNode(node => {
    if (node.id && expandedNodeIds.has(node.id)) {
      node.setExpanded(true)
    }
  })
}

export const useBulkExcelExport = ({
  teams,
  allSprints,
  gridApi,
  customExportValues,
  setLoading,
}: BulkExcelExportParams): {
  onBulkExport: (selectedColIds: string[]) => Promise<void>
} => {
  const { getByTeamUuid } = useProductBacklogRepository()
  const dispatch = useDispatch()
  const intl = useIntl()

  const onBulkExport = useCallback(
    async (selectedColIds: string[]) => {
      if (!gridApi || teams.length === 0 || selectedColIds.length === 0) return
      setLoading(true)

      try {
        const allRows = await fetchAllTeamRows(teams, allSprints, getByTeamUuid)
        if (allRows.length === 0) return

        const snapshot = captureGridState(gridApi)

        // postSortRows をチーム名ソートに一時差し替え
        // デフォルトの postSortRows は未設定スプリントを末尾に移動するが、
        // 一括出力時はチーム単位でまとめるためチーム名でソートする
        const originalPostSortRows =
          gridApi.getGridOption('postSortRows') ?? undefined
        gridApi.setGridOption(
          'postSortRows',
          (params: PostSortRowsParams<ProductBacklogRow>) => {
            const nodes = params.nodes
            if (!nodes || nodes.length === 0) return
            nodes.sort((a, b) => {
              const teamA = a.data?.teamName ?? ''
              const teamB = b.data?.teamName ?? ''
              return teamA.localeCompare(teamB)
            })
          }
        )

        try {
          gridApi.setGridOption('rowData', allRows)
          // AG Grid の setGridOption('rowData') は非同期でDOMを更新するため、
          // エクスポート前にマイクロタスクを挟んでグリッドの描画完了を待つ
          await new Promise<void>(resolve => setTimeout(resolve, 0))

          exportExcel({
            fileNamePrefix: 'productBacklog_allTeams',
            gridApi,
            exportColIds: selectedColIds,
            getCustomExportValue: wrapWithRowNumber(customExportValues),
          })
        } finally {
          await restoreGridState(
            gridApi,
            snapshot.rowData,
            snapshot.expandedNodeIds,
            originalPostSortRows
          )
        }
      } catch (error) {
        console.error('Bulk export failed:', error)
        dispatch(
          addGlobalMessage({
            type: MessageLevel.WARN,
            title: intl.formatMessage({
              id: 'productBacklog.bulkExport.error',
            }),
          })
        )
      } finally {
        setLoading(false)
      }
    },
    [
      teams,
      allSprints,
      getByTeamUuid,
      gridApi,
      customExportValues,
      setLoading,
      dispatch,
      intl,
    ]
  )

  return { onBulkExport }
}
